import React, { useContext, useEffect, useState } from "react";
import { fabric } from "@utils/fabric";
import { AppContext } from "../../context/AppContext";
import PropertyLabel from "../Properties/shared/PropertyLabel";
import PropertyHint from "./shared/PropertyHint";
import NumberInput from "./shared/NumberInput";
import { useTranslation } from "react-i18next";

function TextMode() {
  const { canvas, selectedObject } = useContext(AppContext);
  const { t } = useTranslation();

  const [textMode, setTextMode] = useState(
    (selectedObject as fabric.Textbox).get("textMode") ||
      fabric.Textbox.prototype.textMode
  );

  const [maxLines, setMaxLines] = useState(
    (selectedObject as fabric.Textbox).get("maxLines") ||
      fabric.Textbox.prototype.maxLines
  );

  const [showMaxLines, setShowMaxLines] = useState(false);

  // triggered by clicking layers
  useEffect(() => {
    const maxLinesValue =
      (selectedObject as fabric.Textbox).get("maxLines") ||
      fabric.Textbox.prototype.maxLines;

    const textModeValue =
      (selectedObject as fabric.Textbox).get("textMode") ||
      fabric.Textbox.prototype.textMode;

    setTextMode(textModeValue);
    setMaxLines(maxLinesValue);
  }, [selectedObject]);

  useEffect(() => {
    if (textMode === "autofit") {
      setShowMaxLines(false);
    } else {
      setShowMaxLines(true);
    }
  }, [textMode]);

  function onTextModeChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const value = event.target.value as "autofit" | "truncate";
    setTextMode(value);
    (selectedObject as fabric.Textbox).set({ textMode: value });
    canvas?.renderAll();
  }

  function onMaxLinesChange(event: React.ChangeEvent<HTMLInputElement>) {
    const value = Math.max(0, parseInt(event.target.value, 10));
    setMaxLines(value);
    (selectedObject as fabric.Textbox).set({ maxLines: value });
    canvas?.renderAll();
  }

  return (
    <>
      <div>
        <PropertyLabel>{t("text_mode_label")}</PropertyLabel>
        <select
          onChange={onTextModeChange}
          value={textMode}
          className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
        >
          <option value="autofit">{t("text_mode_autofit")}</option>
          <option value="truncate">{t("text_mode_truncate")}</option>
        </select>
        <PropertyHint>
          <ul className="list-disc list-inside space-y-1">
            <li>
              <span className="font-medium text-gray-700">
                {t("text_mode_autofit_label")}
              </span>{" "}
              {t("text_mode_autofit_help")}
            </li>
            <li>
              <span className="font-medium text-gray-700">
                {t("text_mode_truncate_label")}
              </span>{" "}
              {t("text_mode_truncate_help")}
            </li>
          </ul>
        </PropertyHint>
      </div>
      {showMaxLines && (
        <div>
          <PropertyLabel>{t("text_mode_line_clamp")}</PropertyLabel>
          <NumberInput
            onChange={onMaxLinesChange}
            value={maxLines}
            min={0}
            step={1}
          />
          <PropertyHint>
            {t("text_mode_line_clamp_help")}
          </PropertyHint>
        </div>
      )}
    </>
  );
}

export default TextMode;
